/// <reference path="../vite-env.d.ts" />
import { useEffect, useState } from 'react';
import { useKeycloak } from "@react-keycloak/web";
import { useProjectsStore } from "../state/projects";
import { UnifiedTask, convertProjectItemToUnifiedTask, convertActionItemToUnifiedTask } from '../types/task';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router';
import { BsThreeDotsVertical, BsArrowDown, BsArrowUp } from 'react-icons/bs';
import { CreateDropdown } from '../components/CreateDropdown';
import { ValidateRole } from '../helpers/ValidateRole';
import { NewProjectForm } from '../components/NewProjectForm';
import { NotificationButton } from '../components/NotificationButton';

type SortField = 'dueDate' | 'status' | 'priority';
type SortDirection = 'asc' | 'desc';

const Tasks = () => {
  useKeycloak();
  const navigate = useNavigate();
  const projects = useProjectsStore((state) => state.projects);
  const [tasks, setTasks] = useState<UnifiedTask[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [showNewProject, setShowNewProject] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    if (!projects) return;

    const allTasks: UnifiedTask[] = [];
    
    projects.forEach((project: ProjectInterface) => {
      // Add Project Plan items
      if (project.items) {
        project.items.forEach((item: ProjectItemInterface) => {
          allTasks.push(convertProjectItemToUnifiedTask(item, project));
        });
      }

      // Add Action Items
      if ((project as any).actionItems) {
        (project as any).actionItems.forEach((item: ProjectActionItem) => {
          allTasks.push(convertActionItemToUnifiedTask(item, project));
        });
      }
    });

    setTasks(allTasks);
  }, [projects]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedTasks = (tasksToSort: UnifiedTask[]) => {
    return [...tasksToSort].sort((a, b) => {
      const multiplier = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'dueDate':
          return (new Date(a.dueDate || '').getTime() - new Date(b.dueDate || '').getTime()) * multiplier;
        case 'status':
          return (a.status || '').localeCompare(b.status || '') * multiplier;
        case 'priority':
          const priorityOrder: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 };
          return ((priorityOrder[a.priority || ''] || 0) - (priorityOrder[b.priority || ''] || 0)) * multiplier;
        default:
          return 0;
      }
    });
  };

  const filteredTasks = tasks.filter(task => {
    if (selectedProject !== 'all' && task.projectId !== selectedProject) return false;
    if (selectedStatus !== 'all' && task.status !== selectedStatus) return false;
    if (selectedPriority !== 'all' && task.priority !== selectedPriority) return false;
    return true;
  });

  const sortedAndFilteredTasks = getSortedTasks(filteredTasks);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-primary/10 text-primary';
      case 'NOT_STARTED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-orange-100 text-orange-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceLabel = (source: 'project_plan' | 'action_item') => {
    return source === 'project_plan' ? 'Project Plan' : 'Action Item';
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <BsArrowUp className="inline ml-1" /> : <BsArrowDown className="inline ml-1" />;
  };

  return (
    <div className="flex flex-col w-full h-full p-6">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tasks</h1>
        <div className="flex items-center gap-4">
          <ValidateRole userRoles={['agency-admin']} expectedRoles={['agency-admin']}>
            <CreateDropdown onSelectProject={() => setShowNewProject(true)} />
          </ValidateRole>
          <NotificationButton />
          <select
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm"
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
        </div>
      </div>
      <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phase
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date <SortIcon field="dueDate" />
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('status')}
                >
                  Status <SortIcon field="status" />
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('priority')}
                >
                  Priority <SortIcon field="priority" />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedAndFilteredTasks.map((task) => (
                <tr key={`${task.source}-${task.id}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{task.name}</div>
                    {task.description && (
                      <div className="text-sm text-gray-500 mt-1">{task.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link 
                      to={`/projects/${task.projectId}`}
                      className="text-sm text-gray-900 hover:text-primary"
                    >
                      {task.projectName}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.phaseName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(task.status)}`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getSourceLabel(task.source)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-gray-400 hover:text-gray-600">
                      <BsThreeDotsVertical className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Tasks; 